// <athos-collection> grid: AthosClient supplies data, the theme renders jam-cards via ?view=card.
// The URL is the only state we own; toggling a filter writes the URL and reloads.
(() => {
  if (customElements.get('athos-collection')) return;

  const readFilters = (params) => {
    const filters = [];
    params.forEach((value, key) => {
      const field = key.match(/^filter\.(.+)$/);
      if (field && value) filters.push({ field: field[1], value });
    });
    return filters;
  };

  /* The page a fresh load starts on, from ?page=N (1 when absent/invalid). */
  const readPage = (params) => {
    const page = Number.parseInt(params.get('page'), 10);
    return Number.isFinite(page) && page > 1 ? page : 1;
  };

  /* Strip the inert widget script from a merchandising banner, keep the rendered anchor. */
  const bannerMarkup = (value) => {
    const template = document.createElement('template');
    template.innerHTML = String(value || '');
    template.content.querySelectorAll('script').forEach((node) => node.remove());
    return template.innerHTML.trim();
  };

  const readJsonScript = (root, selector, fallback) => {
    const node = root.querySelector(selector) || document.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent) ?? fallback;
    } catch (_error) {
      return fallback;
    }
  };

  class AthosCollection extends HTMLElement {
    connectedCallback() {
      if (!window.AthosClient || !window.AthosCards) return;
      if (!this.dataset.siteId) {
        console.error('[athos-collection] Missing Athos site ID. Set it in Theme settings → Athos / Searchspring → “Athos site ID”.');
        return;
      }
      this.client = window.AthosClient.createClient({
        siteId: this.dataset.siteId,
        ajaxCatalog: this.dataset.ajaxCatalog,
      });

      this.grid = this.querySelector('[data-athos-grid]');
      this.countEl = this.querySelector('[data-athos-count]');
      this.emptyEl = this.querySelector('[data-athos-empty]');
      this.errorEl = this.querySelector('[data-athos-error]');
      this.errorMessageEl = this.querySelector('.athos-collection__error-message');
      this.paginationEl = this.querySelector('[data-athos-pagination]');
      this.facetListEl = this.querySelector('[data-athos-facet-list]');
      this.chipsEl = this.querySelector('[data-athos-chips]');
      this.sortEl = this.querySelector('[data-athos-sort]');
      this.filterCountEl = this.querySelector('[data-athos-filter-count]');
      this.panel = this.querySelector('[data-athos-facet-panel]');
      this.backdrop = this.querySelector('[data-athos-backdrop]');

      this.perPage = Number.parseInt(this.dataset.perPage, 10) || 24;
      /* Sort applied when the URL carries no sort ('field|dir', '' = Featured). */
      this.defaultSort = this.dataset.defaultSort || '';
      /* Search mode scopes the grid by the q query instead of a collection. */
      this.searchMode = this.dataset.searchTerms !== undefined;
      this.inlineBanners = this.dataset.inlineBanners !== 'false';
      this.colorMap = readJsonScript(this, '[data-athos-swatch-map]', {});

      /* Curated filter blocks in the merchant's order. Empty means auto: every facet Athos returns. */
      this.facetConfig = readJsonScript(this, '[data-athos-facets-config]', []);

      /* Facet behaviour driven by section settings (see the section schema). */
      const num = (value, fallback) => {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      this.facetOptions = {
        colorMap: this.colorMap,
        valueLimit: num(this.dataset.facetValueLimit, 8),
        searchThreshold: num(this.dataset.facetSearchThreshold, 15),
        colorSwatches: this.dataset.colorSwatches !== 'false',
        forceCollapse: this.dataset.collapseFilters !== 'false',
        hideSingleValue: this.dataset.hideSingleValue === 'true',
        /* colorMap key order is the swatch priority (the color_swatches metaobject order). */
        swatchPriority: Object.keys(this.colorMap || {}),
        swatchValueLimit: num(this.dataset.swatchValueLimit, 30),
        booleanYesNo: this.dataset.booleanYesNo === 'true',
      };

      /* firstPage is the entry page (anchors "Previous"); page is the last page loaded. */
      this.firstPage = readPage(new URLSearchParams(window.location.search));
      this.page = this.firstPage;
      this.totalPages = 1;
      this.facetsRendered = false;
      this.loading = false;

      /* Aborts in-flight card fetches when the element leaves the DOM. */
      this.lifecycleController = new AbortController();
      this.requestGeneration = 0;
      this.lifecycleController.signal.addEventListener('abort', () => this.requestController?.abort(), { once: true });

      this.bindEvents();
      this.loadPage(true);
    }

    disconnectedCallback() {
      this.lifecycleController?.abort();
    }

    bindEvents() {
      this.sortEl?.addEventListener('change', () => {
        this.syncSortLabel();
        this.applySort(this.sortEl.value);
      });

      /* Delegated listeners so a re-render never leaves stale handlers behind. */
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-athos-facet-input]');
        if (input) this.toggleFilter(input.dataset.field, input.dataset.value, input.checked);
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-athos-retry]')) return this.retryLastPage();
        if (event.target.closest('[data-athos-clear-all]')) return this.clearAll();
        const chip = event.target.closest('[data-athos-chip-remove]');
        if (chip) return this.removeFilter(chip.dataset.field, chip.dataset.value);
        const more = event.target.closest('[data-athos-facet-more]');
        if (more) return this.expandFacet(more);
        if (event.target.closest('[data-athos-filter-toggle]')) return this.toggleFilters();
        if (event.target.closest('[data-athos-filter-close]') || event.target.closest('[data-athos-backdrop]')) return this.closePanel();
      });

      this.addEventListener('input', (event) => {
        const search = event.target.closest('[data-athos-facet-search]');
        if (search) this.searchFacet(search);
      });

      window.addEventListener('popstate', () => this.reload());

      /* Crossing the xl breakpoint tears down any open drawer and relabels the toggle. */
      this.mq = window.matchMedia('(min-width: 1280px)');
      this.mq.addEventListener('change', () => this.closePanel());
      this.syncFilterToggle();
      this.syncPanelInert();
    }

    isDesktop() {
      return this.mq ? this.mq.matches : window.matchMedia('(min-width: 1280px)').matches;
    }

    toggleFilters() {
      if (this.isDesktop()) {
        this.classList.toggle('athos-collection--filters-hidden');
      } else if (this.classList.contains('athos-collection--panel-open')) {
        this.closePanel();
        return;
      } else {
        this.openPanel();
        return;
      }
      this.syncFilterToggle();
    }

    syncFilterToggle() {
      const label = this.querySelector('[data-athos-filter-label]');
      const button = this.querySelector('[data-athos-filter-toggle]');
      if (this.isDesktop()) {
        const hidden = this.classList.contains('athos-collection--filters-hidden');
        if (label) label.textContent = hidden ? 'Show Filters' : 'Hide Filters';
        button?.setAttribute('aria-expanded', String(!hidden));
      } else {
        const open = this.classList.contains('athos-collection--panel-open');
        if (label) label.textContent = 'Filters';
        button?.setAttribute('aria-expanded', String(open));
      }
    }

    /* The sort as 'field|dir' ('' = Featured). URL sort wins, then ?sort=featured, then the default. */
    effectiveSortValue(params) {
      const urlValue = window.AthosFacets.readSortValue(params);
      if (urlValue) return urlValue;
      if (params.get('sort') === 'featured') return '';
      return this.defaultSort;
    }

    request(page = this.page) {
      const params = new URLSearchParams(window.location.search);
      return {
        /* q in the URL is the source of truth; the data attr is the initial server value. */
        searchTerms: params.get('q') ?? this.dataset.searchTerms ?? '',
        collectionHandle: this.dataset.collectionHandle,
        collectionId: this.dataset.collectionId,
        page,
        resultsPerPage: this.perPage,
        filters: readFilters(params),
        sort: window.AthosClient.parseSortValue(this.effectiveSortValue(params)),
      };
    }

    removeSkeletons() {
      this.grid?.querySelectorAll('[data-athos-skeleton]').forEach((node) => node.remove());
    }

    async reload() {
      /* A filter/sort change starts over at page 1. */
      this.page = 1;
      this.firstPage = 1;
      if (this.grid) this.grid.innerHTML = '';
      await this.loadPage(true);
    }

    async loadNext() {
      if (this.loading || this.page >= this.totalPages) return;
      await this.loadPage(false, this.page + 1);
    }

    async retryLastPage() {
      if (this.loading) return;
      await this.loadPage(this.lastRequestWasReset, this.lastRequestedPage ?? this.page);
    }

    beginRequest() {
      this.requestController?.abort();
      this.requestController = new AbortController();
      this.requestGeneration += 1;
      return { generation: this.requestGeneration, signal: this.requestController.signal };
    }

    isCurrentRequest(generation) {
      return generation === this.requestGeneration && !this.lifecycleController.signal.aborted;
    }

    async loadPage(reset, requestedPage = this.page) {
      const { generation, signal } = this.beginRequest();
      this.lastRequestedPage = requestedPage;
      this.lastRequestWasReset = reset;
      this.setBusy(true);
      this.hideLoadError();
      try {
        const request = this.request(requestedPage);
        const data = await (this.searchMode
          ? this.client.fetchSearchPage(request, { signal })
          : this.client.fetchCategoryPage(request, { signal }));
        if (!this.isCurrentRequest(generation)) return;
        const results = Array.isArray(data?.results) ? data.results : [];
        const pagination = data?.pagination || {};
        const rawTotal = Number.parseInt(pagination.totalResults ?? results.length, 10) || 0;
        /* Count is products only; paging stays on rawTotal, Athos's slot total. */
        const total = pagination.productResults ?? results.length;
        this.totalPages = Number.parseInt(pagination.totalPages ?? Math.ceil(rawTotal / this.perPage), 10) || 1;

        /* Paint controls from the response before cards load; only on a fresh load, not load-more. */
        if (reset) this.renderControls(data);
        if (reset) this.updateCanonical();
        this.renderCount(total);
        if (total === 0) this.removeSkeletons();
        if (this.emptyEl) this.emptyEl.hidden = total !== 0;

        const handles = results
          .map((result) => window.AthosClient.handleFromResult(result))
          .filter(Boolean);
        const cards = await window.AthosCards.fetchCards(handles, { signal });
        if (!this.isCurrentRequest(generation)) return;

        const rendered = cards.filter(Boolean);
        if (handles.length > 0 && rendered.length === 0) throw new Error('athos-collection: every card request failed');

        this.page = requestedPage;
        this.removeSkeletons();
        rendered.forEach((html) => this.grid.insertAdjacentHTML('beforeend', html));
        window.DisplayGroupSwatches?.register(this.grid);
        this.updatePagination();
        /* Banners slot in by index only on a reset, so load-more never shifts a placed banner. */
        if (reset) this.injectBanners(data);
      } catch (_error) {
        if (this.isCurrentRequest(generation)) this.showLoadError();
      } finally {
        if (this.isCurrentRequest(generation)) this.setBusy(false);
      }
    }

    showLoadError() {
      this.removeSkeletons();
      this.updatePagination();
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      window.theme?.a11y?.announce(this.errorMessageEl?.textContent.trim());
    }

    hideLoadError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    renderControls(data) {
      const params = new URLSearchParams(window.location.search);

      if (this.facetListEl && window.AthosFacets) {
        const snapshot = this.snapshotFacets();
        this.facetListEl.innerHTML = window.AthosFacets.facetListHtml(this.selectFacets(data?.facets), {
          ...this.facetOptions,
          openFields: this.facetsRendered ? snapshot.open : undefined,
        });
        this.restoreFacets(snapshot);
        this.facetsRendered = true;
      }

      if (this.chipsEl && window.AthosFacets) {
        this.chipsEl.innerHTML = window.AthosFacets.chipsHtml(data?.filterSummary, { booleanYesNo: this.facetOptions.booleanYesNo });
        this.chipsEl.hidden = this.chipsEl.childElementCount === 0;
      }

      if (this.sortEl && window.AthosFacets) {
        this.sortEl.innerHTML = window.AthosFacets.sortOptionsHtml(data?.sorting, this.effectiveSortValue(params));
        this.syncSortLabel();
      }

      const activeCount = Array.isArray(data?.filterSummary) ? data.filterSummary.length : 0;
      if (this.filterCountEl) {
        this.filterCountEl.textContent = String(activeCount);
        this.filterCountEl.hidden = activeCount === 0;
      }
    }

    /* With curated blocks, render only those facets in block order with their overrides. */
    selectFacets(facets) {
      const all = Array.isArray(facets) ? facets : [];
      if (!this.facetConfig.length) return all;

      const normalize = window.AthosClient?.normalizeField || ((field) => field);
      const byField = new Map(all.map((facet) => [normalize(facet.field), facet]));

      return this.facetConfig
        .map((config) => {
          const facet = byField.get(normalize(config.field));
          if (!facet) return undefined;
          return {
            ...facet,
            label: config.label || facet.label,
            presentation: config.presentation,
            collapseOverride: typeof config.collapsed === 'boolean' ? config.collapsed : undefined,
          };
        })
        .filter(Boolean);
    }

    /* Preserve open/expanded facets and scroll position across a re-render. */
    snapshotFacets() {
      const list = this.facetListEl;
      return {
        open: new Set(Array.from(list.querySelectorAll('details[data-field][open]')).map((el) => el.dataset.field)),
        expanded: new Set(Array.from(list.querySelectorAll('[data-athos-facet-more][aria-expanded="true"]')).map((el) => el.closest('[data-field]')?.dataset.field)),
        scrollTop: this.panel?.scrollTop || 0,
      };
    }

    restoreFacets({ expanded, scrollTop }) {
      expanded.forEach((field) => {
        const more = this.facetListEl.querySelector(`[data-field="${CSS.escape(field)}"] [data-athos-facet-more]`);
        if (more) this.expandFacet(more);
      });
      if (this.panel && scrollTop) this.panel.scrollTop = scrollTop;
    }

    expandFacet(button) {
      const list = button.parentElement.querySelector('[data-athos-facet-options]');
      list?.classList.add('is-expanded');
      button.setAttribute('aria-expanded', 'true');
      button.hidden = true;
    }

    searchFacet(input) {
      const list = input.parentElement.querySelector('[data-athos-facet-options]');
      if (!list) return;
      const query = input.value.trim().toLowerCase();
      list.classList.toggle('is-searching', query.length > 0);
      list.querySelectorAll('[data-athos-facet-value]').forEach((li) => {
        li.hidden = query ? !String(li.dataset.label || '').includes(query) : false;
      });
    }

    /* Drop each inline banner into the grid at its configured index, lowest first. */
    injectBanners(data) {
      if (!this.inlineBanners || !this.grid) return;
      const banners = data?.merchandising?.content?.inline;
      if (!Array.isArray(banners) || !banners.length) return;

      banners
        .slice()
        .sort((a, b) => (a?.config?.position?.index ?? 0) - (b?.config?.position?.index ?? 0))
        .forEach((banner) => {
          const markup = bannerMarkup(banner?.value);
          if (!markup) return;
          const cell = document.createElement('div');
          cell.className = 'athos-collection__inline-banner';
          cell.setAttribute('role', 'presentation');
          cell.innerHTML = markup;
          const index = Number.parseInt(banner?.config?.position?.index, 10);
          const ref = Number.isFinite(index) ? this.grid.children[index] : undefined;
          this.grid.insertBefore(cell, ref || null);
        });
    }

    toggleFilter(field, value, checked) {
      const params = new URLSearchParams(window.location.search);
      const key = `filter.${field}`;
      const kept = params.getAll(key).filter((existing) => existing !== value);
      params.delete(key);
      kept.forEach((existing) => params.append(key, existing));
      if (checked) params.append(key, value);
      params.delete('page');
      this.commit(params);
    }

    removeFilter(field, value) {
      this.toggleFilter(field, value, false);
    }

    applySort(value) {
      const params = new URLSearchParams(window.location.search);
      Array.from(new Set(params.keys()))
        .filter((key) => key.startsWith('sort.') || key === 'sort')
        .forEach((key) => params.delete(key));
      if (value) {
        const [field, direction] = value.split('|');
        if (field) params.set(`sort.${field}`, direction === 'desc' ? 'desc' : 'asc');
      } else if (this.defaultSort) {
        /* Record Featured explicitly so it holds on reload instead of falling back to the default. */
        params.set('sort', 'featured');
      }
      params.delete('page');
      this.commit(params);
    }

    clearAll() {
      const params = new URLSearchParams(window.location.search);
      Array.from(new Set(params.keys()))
        .filter((key) => key.startsWith('filter.') || key.startsWith('sort.') || key === 'sort' || key === 'page')
        .forEach((key) => params.delete(key));
      this.commit(params);
    }

    commit(params) {
      const query = params.toString();
      window.history.pushState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
      this.closePanel();
      this.reload();
    }

    /* As a mobile drawer the panel behaves like a modal dialog; the dialog role/inert live only while open. */
    openPanel() {
      this.classList.add('athos-collection--panel-open');
      if (this.backdrop) this.backdrop.hidden = false;
      this.panel?.setAttribute('role', 'dialog');
      this.panel?.setAttribute('aria-modal', 'true');
      this.syncPanelInert();
      this.syncFilterToggle();

      this.trapController = new AbortController();
      document.addEventListener('keydown', (event) => this.onPanelKeydown(event), { signal: this.trapController.signal });
      (this.querySelector('[data-athos-filter-close]') || this.focusablePanelItems()[0] || this.panel)?.focus();
    }

    closePanel() {
      const wasOpen = this.classList.contains('athos-collection--panel-open');
      this.classList.remove('athos-collection--panel-open');
      if (this.backdrop) this.backdrop.hidden = true;
      this.panel?.removeAttribute('role');
      this.panel?.removeAttribute('aria-modal');
      this.trapController?.abort();
      this.trapController = undefined;
      this.syncPanelInert();
      this.syncFilterToggle();
      /* Restore focus to the toggle only for a real drawer close. */
      if (wasOpen && !this.isDesktop()) this.querySelector('[data-athos-filter-toggle]')?.focus();
    }

    /* inert takes the closed drawer out of the tab order and a11y tree; desktop uses display:none. */
    syncPanelInert() {
      if (!this.panel) return;
      this.panel.inert = !this.isDesktop() && !this.classList.contains('athos-collection--panel-open');
    }

    focusablePanelItems() {
      if (!this.panel) return [];
      const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      /* offsetWidth+offsetHeight drops display:none items (collapsed facets, search-hidden values). */
      return Array.from(this.panel.querySelectorAll(selector)).filter((el) => !el.hidden && el.offsetWidth + el.offsetHeight > 0);
    }

    onPanelKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePanel();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = this.focusablePanelItems();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !this.panel.contains(active);
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    }

    syncSortLabel() {
      const label = this.querySelector('[data-athos-sort-label]');
      const select = this.sortEl;
      if (label && select) label.textContent = select.options[select.selectedIndex]?.text || 'Featured';
    }

    renderCount(total) {
      if (!this.countEl) return;
      const template = this.dataset.countLabel || '{count} products';
      this.countEl.textContent = template.replace('{count}', String(total));
    }

    setBusy(busy) {
      this.loading = busy;
      this.toggleAttribute('aria-busy', busy);
    }

    /* A page URL preserving the current filters/sort. Page 1 is the clean URL. */
    pageHref(page) {
      const params = new URLSearchParams(window.location.search);
      if (page > 1) params.set('page', String(page));
      else params.delete('page');
      const query = params.toString();
      return query ? `${window.location.pathname}?${query}` : window.location.pathname;
    }

    /* Rebuild the crawlable prev/next anchors after each load; Load more is the next anchor intercepted to append. */
    updatePagination() {
      const nav = this.paginationEl;
      if (!nav) return;
      const hasPrev = this.firstPage > 1;
      const hasNext = this.page < this.totalPages;
      nav.replaceChildren();

      if (hasPrev) {
        const prev = document.createElement('a');
        prev.className = 'button button--secondary athos-collection__page-link';
        prev.rel = 'prev';
        prev.href = this.pageHref(this.firstPage - 1);
        prev.textContent = 'Previous';
        nav.appendChild(prev);
      }
      if (hasNext) {
        const next = document.createElement('a');
        next.className = 'button button--secondary athos-collection__load-more';
        next.rel = 'next';
        next.href = this.pageHref(this.page + 1);
        next.setAttribute('data-athos-load-more', '');
        next.textContent = 'Load more';
        next.addEventListener('click', (event) => {
          event.preventDefault();
          this.loadNext();
        });
        nav.appendChild(next);
      }
      nav.hidden = !hasPrev && !hasNext;
    }

    /* Canonical: filtered views collapse to the base collection; unfiltered deep pages self-canonical to ?page=N. */
    updateCanonical() {
      if (this.searchMode) return;
      const link = document.querySelector('link[rel="canonical"]');
      if (!link) return;
      const params = new URLSearchParams(window.location.search);
      const hasFilters = Array.from(params.keys()).some((key) => key.startsWith('filter.'));
      let href = window.location.origin + window.location.pathname;
      if (!hasFilters && this.firstPage > 1) href += `?page=${this.firstPage}`;
      link.href = href;
    }
  }

  customElements.define('athos-collection', AthosCollection);
})();
